import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getUser } from '../utils/authUtil';
import './DashboardPro.css';

const scoreColor = (v) => (v == null ? '#94a3b8' : v >= 75 ? '#16a34a' : v >= 60 ? '#2563eb' : v >= 40 ? '#d97706' : '#dc2626');
const fmt = (v, suffix = '') => (v == null ? '—' : `${v}${suffix}`);
const READINESS_LABEL = { top_performer: 'Top Performer', placement_ready: 'Placement Ready', moderate: 'Moderate', needs_improvement: 'Needs Improvement' };

const StatCard = ({ label, value, suffix = '', sub, tone, big }) => (
  <div className={`dpx-stat ${big ? 'big' : ''}`}>
    <div className="dpx-stat-value" style={{ color: tone }}>{fmt(value, suffix)}</div>
    <div className="dpx-stat-label">{label}</div>
    {sub != null && <div className="dpx-stat-sub">{sub}</div>}
  </div>
);

const Bars = ({ items, labelKey, valueKey, max = 100, unit = '%' }) => (
  <div className="dpx-bars">
    {items.length === 0 ? <div className="dpx-empty">No data yet</div> : items.map((it, i) => (
      <div className="dpx-bar-row" key={i}>
        <span className="dpx-bar-label">{it[labelKey]}</span>
        <div className="dpx-bar-track"><div className="dpx-bar-fill" style={{ width: `${Math.min((it[valueKey] || 0) / max * 100, 100)}%`, background: scoreColor(it[valueKey]) }} /></div>
        <span className="dpx-bar-val" style={{ color: scoreColor(it[valueKey]) }}>{it[valueKey] == null ? '—' : `${it[valueKey]}${unit}`}</span>
      </div>
    ))}
  </div>
);

const Section = ({ title, sub, children }) => (
  <section className="dpx-section">
    <div className="dpx-section-head"><h2>{title}</h2>{sub && <p>{sub}</p>}</div>
    {children}
  </section>
);

const Dashboard = () => {
  const user = getUser();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get('/reports/dashboard');
        setD(res.data);
      } catch (e) { setErr(e.response?.data?.message || 'Failed to load dashboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="dpx-page"><div className="dpx-loading">Loading college analytics…</div></div>;
  if (err || !d) return <div className="dpx-page"><div className="dpx-empty-box">⚠️ {err || 'No data'}</div></div>;
  if (d.scoped === false) return <div className="dpx-page"><div className="dpx-empty-box">{d.message}</div></div>;

  const { executive: ex, college_health: h, learning: l, behaviour: b, placement: p, intervention: iv, growth: g, system: sys } = d;
  const distToChart = (dist) => (dist || []).map(x => ({ name: x.label.split(' ')[0], count: x.count }));

  return (
    <div className="dpx-page">
      <div className="dpx-topbar">
        <div><h1>College Dashboard</h1><p>Welcome back, {user?.name}</p></div>
        <div className="dpx-scope">Diagnostic coverage: <strong>{fmt(ex.diagnostic_completion_pct, '%')}</strong> ({ex.diagnosed_students}/{ex.total_students})</div>
      </div>

      {/* SECTION 1 — Executive Summary */}
      <Section title="Executive Summary" sub="College at a glance">
        <div className="dpx-stat-grid">
          <StatCard label="Total Students" value={ex.total_students} big />
          <StatCard label="Diagnostic Completion" value={ex.diagnostic_completion_pct} suffix="%" tone={scoreColor(ex.diagnostic_completion_pct)} />
          <StatCard label="Placement Ready" value={ex.placement_ready} tone="#16a34a" sub={`of ${ex.diagnosed_students} diagnosed`} />
          <StatCard label="Avg Knowledge" value={ex.avg_knowledge} suffix="%" tone={scoreColor(ex.avg_knowledge)} />
          <StatCard label="Avg Behaviour" value={ex.avg_behaviour} suffix="%" tone={scoreColor(ex.avg_behaviour)} />
          <StatCard label="Avg ERI" value={ex.avg_eri} suffix="%" tone={scoreColor(ex.avg_eri)} />
          <StatCard label="Need Attention" value={ex.students_needing_attention} tone="#dc2626" />
        </div>
      </Section>

      {/* SECTION 2 — College Health */}
      <Section title="College Health" sub="Distributions across diagnosed students">
        <div className="dpx-grid-3">
          {[['Knowledge', h.knowledge_distribution], ['Behaviour', h.behaviour_distribution], ['ERI', h.eri_distribution]].map(([name, dist]) => (
            <div className="dpx-card" key={name}>
              <h3>{name} Distribution</h3>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={distToChart(dist)} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip /><Bar dataKey="count" radius={[4, 4, 0, 0]}>{distToChart(dist).map((_, i) => <Cell key={i} fill={['#dc2626', '#d97706', '#2563eb', '#16a34a'][i]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
        <div className="dpx-grid-2">
          <div className="dpx-card">
            <h3>Readiness Levels</h3>
            <Bars items={(h.readiness_levels || []).map(r => ({ label: READINESS_LABEL[r.level] || r.level, count: r.count }))} labelKey="label" valueKey="count" max={Math.max(1, ...(h.readiness_levels || []).map(r => r.count))} unit="" />
          </div>
          <div className="dpx-card">
            <h3>Department Comparison</h3>
            <table className="dpx-table"><thead><tr><th>Branch</th><th>Diagnosed</th><th>Knowledge</th><th>ERI</th></tr></thead>
              <tbody>{(h.department_comparison || []).map((dep, i) => (
                <tr key={i}><td>{dep.branch}</td><td>{dep.diagnosed}</td><td style={{ color: scoreColor(dep.avg_knowledge) }}>{fmt(dep.avg_knowledge, '%')}</td><td style={{ color: scoreColor(dep.avg_eri) }}>{fmt(dep.avg_eri, '%')}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* SECTION 3 — Learning Analytics */}
      <Section title="Learning Analytics" sub="Topic mastery & difficulty performance (diagnostic tests)">
        <div className="dpx-inline-stats">
          <StatCard label="Question Success Rate" value={l.question_success_rate} suffix="%" tone={scoreColor(l.question_success_rate)} />
          <StatCard label="Avg Time / Question" value={l.avg_time_per_question_sec} suffix="s" />
        </div>
        <div className="dpx-grid-2">
          <div className="dpx-card"><h3>Weakest Topics</h3><Bars items={l.weakest_topics || []} labelKey="topic" valueKey="accuracy" /></div>
          <div className="dpx-card"><h3>Strongest Topics</h3><Bars items={l.strongest_topics || []} labelKey="topic" valueKey="accuracy" /></div>
        </div>
        <div className="dpx-card"><h3>Difficulty-wise Accuracy</h3>
          <Bars items={(l.difficulty_performance || []).map(x => ({ label: `${x.difficulty} (${x.attempted})`, accuracy: x.accuracy }))} labelKey="label" valueKey="accuracy" />
        </div>
      </Section>

      {/* SECTION 4 — Behaviour Analytics */}
      <Section title="Behaviour Analytics" sub="Cognitive-behavioural signals (lower confidence than knowledge)">
        <div className="dpx-stat-grid">
          <StatCard label="Avg TPSI" value={b.avg_tpsi} suffix="%" tone={scoreColor(b.avg_tpsi)} />
          <StatCard label="Avg CER" value={b.avg_cer} suffix="%" tone={scoreColor(b.avg_cer)} />
          <StatCard label="Avg RCI" value={b.avg_rci} suffix="%" tone={scoreColor(b.avg_rci)} />
          <StatCard label="Avg FFI" value={b.avg_ffi} suffix="%" tone={scoreColor(b.avg_ffi)} />
          <StatCard label="Behaviour Score" value={b.avg_behaviour_score} suffix="%" tone={scoreColor(b.avg_behaviour_score)} />
          <StatCard label="Telemetry Coverage" value={b.telemetry_coverage_pct} suffix="%" tone={scoreColor(b.telemetry_coverage_pct)} />
        </div>
        <div className="dpx-chips">
          <span className="dpx-chip">Pressure-sensitive: <b>{b.patterns.pressure_sensitive}</b></span>
          <span className="dpx-chip">Fatiguers: <b>{b.patterns.fatiguers}</b></span>
          <span className="dpx-chip">Speed-dominant: <b>{b.patterns.speed_dominant}</b></span>
          <span className="dpx-chip">RCI unmeasured: <b>{b.patterns.risk_unmeasured_rci}</b></span>
        </div>
      </Section>

      {/* SECTION 5 — Placement Readiness */}
      <Section title="Placement Readiness">
        <div className="dpx-stat-grid">
          <StatCard label="Ready" value={p.ready} tone="#16a34a" />
          <StatCard label="Nearly Ready" value={p.nearly_ready} tone="#2563eb" />
          <StatCard label="Needs Improvement" value={p.needs_improvement} tone="#d97706" />
          <StatCard label="Critical" value={p.critical} tone="#dc2626" />
        </div>
        <div className="dpx-grid-2">
          <div className="dpx-card"><h3>Top Students (by ERI)</h3>
            <table className="dpx-table"><thead><tr><th>#</th><th>Branch</th><th>Knowledge</th><th>ERI</th><th>Level</th></tr></thead>
              <tbody>{(p.top_students || []).map((s, i) => (
                <tr key={i}><td>{i + 1}</td><td>{s.branch || '—'}</td><td>{fmt(s.knowledge, '%')}</td><td style={{ color: scoreColor(s.eri) }}>{fmt(s.eri, '%')}</td><td>{READINESS_LABEL[s.level] || s.level}</td></tr>
              ))}</tbody></table>
          </div>
          <div className="dpx-card"><h3>Most Improved</h3>
            {(p.most_improved || []).length === 0
              ? <div className="dpx-empty">Not enough repeat attempts yet ({p.improvement_trackable_students} students trackable)</div>
              : <table className="dpx-table"><thead><tr><th>Branch</th><th>Attempts</th><th>ERI Δ</th></tr></thead>
                <tbody>{p.most_improved.map((s, i) => <tr key={i}><td>{s.branch}</td><td>{s.attempts}</td><td style={{ color: s.improvement >= 0 ? '#16a34a' : '#dc2626' }}>{s.improvement >= 0 ? '+' : ''}{s.improvement}</td></tr>)}</tbody></table>}
          </div>
        </div>
      </Section>

      {/* SECTION 6 — Intervention Center */}
      <Section title="Intervention Center" sub="Students to prioritise">
        <div className="dpx-stat-grid">
          <StatCard label="Low Knowledge" value={iv.low_knowledge} tone="#dc2626" />
          <StatCard label="Low Behaviour" value={iv.low_behaviour} tone="#dc2626" />
          <StatCard label="Low ERI" value={iv.low_eri} tone="#dc2626" />
          <StatCard label="Declining" value={iv.declining_students} tone="#d97706" />
        </div>
        <div className="dpx-card">
          {(iv.students || []).length === 0 ? <div className="dpx-empty">No students currently flagged for intervention.</div>
            : <table className="dpx-table"><thead><tr><th>Branch</th><th>Knowledge</th><th>Behaviour</th><th>ERI</th><th>Reasons</th></tr></thead>
              <tbody>{iv.students.map((s, i) => (
                <tr key={i}><td>{s.branch || '—'}</td><td>{fmt(s.knowledge, '%')}</td><td>{fmt(s.behaviour, '%')}</td><td style={{ color: scoreColor(s.eri) }}>{fmt(s.eri, '%')}</td><td>{(s.reasons || []).join(', ')}</td></tr>
              ))}</tbody></table>}
        </div>
      </Section>

      {/* SECTION 7 — Growth Analytics */}
      <Section title="Growth Analytics" sub={g.note}>
        <div className="dpx-stat-grid">
          <StatCard label="Trackable Students" value={g.trackable_students} />
          <StatCard label="Avg ERI Improvement" value={g.avg_eri_improvement} tone={g.avg_eri_improvement >= 0 ? '#16a34a' : '#dc2626'} />
          <StatCard label="Improving" value={g.improving_students} tone="#16a34a" />
          <StatCard label="Declining" value={g.declining_students} tone="#dc2626" />
        </div>
      </Section>

      {/* SECTION 8 — System Health */}
      <Section title="System Health">
        <div className="dpx-stat-grid">
          <StatCard label="Total Tests" value={sys.total_tests} />
          <StatCard label="Diagnostic Tests" value={sys.diagnostic_tests} />
          <StatCard label="Question Bank" value={sys.question_bank} />
          <StatCard label="Submitted Attempts" value={sys.total_submitted_attempts} />
          <StatCard label="Avg Completion Time" value={sys.avg_completion_time_sec} suffix="s" />
          <StatCard label="Avg Attempts / Student" value={sys.avg_attempts_per_student} />
          <StatCard label="Telemetry Coverage" value={sys.telemetry_coverage_pct} suffix="%" tone={scoreColor(sys.telemetry_coverage_pct)} />
          <StatCard label="Missing Behaviour Data" value={sys.diagnostic_attempts_without_behaviour} tone="#d97706" />
        </div>
      </Section>

      {(d.data_notes || []).length > 0 && (
        <div className="dpx-notes"><strong>Data notes:</strong><ul>{d.data_notes.map((note, i) => <li key={i}>{note}</li>)}</ul></div>
      )}
    </div>
  );
};

export default Dashboard;
